'use client';

import classNames from 'classnames';
import { type PropsWithChildren, useRef } from 'react';
import {
  type TooltipTriggerProps,
  type AriaTooltipProps,
  mergeProps,
  useTooltip,
  useTooltipTrigger,
  usePress,
  useFocus,
} from 'react-aria';
import { type TooltipTriggerState, useTooltipTriggerState } from 'react-stately';

import styles from './tooltip.module.scss';

type TooltipProps = PropsWithChildren<{ state: TooltipTriggerState } & AriaTooltipProps>;

type ExtendedTooltipTriggerProps = {
  props: TooltipTriggerProps &
    PropsWithChildren<{
      className?: string;
      tooltip: string;
    }>;
};

const Tooltip = ({ state, ...props }: TooltipProps) => {
  const { tooltipProps } = useTooltip(props, state);

  return (
    <span className={styles.tooltip} {...mergeProps(props, tooltipProps)}>
      {props.children}
    </span>
  );
};

export const TooltipTrigger = (props: ExtendedTooltipTriggerProps['props']) => {
  const state = useTooltipTriggerState(props);
  const ref = useRef(null);

  const { pressProps } = usePress({
    onPressEnd: () => state.close(),
    onPressStart: () => state.open(),
  });

  const { focusProps } = useFocus({
    onBlur: () => state.close(),
    onFocus: () => state.open(),
  });

  const { tooltipProps, triggerProps } = useTooltipTrigger(props, state, ref);

  return (
    <div
      {...triggerProps}
      {...pressProps}
      {...focusProps}
      aria-label={props.tooltip}
      className={classNames(styles['tooltip-trigger'], props.className)}
      ref={ref}
      tabIndex={0}
    >
      {props.children}
      {state.isOpen && (
        <Tooltip state={state} {...tooltipProps}>
          {props.tooltip}
        </Tooltip>
      )}
    </div>
  );
};
